import React, { useEffect, useMemo, useState } from 'react'

import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'

import './penalty.css'

type AnalyticsRecord = Record<string, unknown>

function formatInr(n: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n)
}

/** Same-origin + optional `REACT_APP_API_BASE_URL` join (reduces gateway/proxy timeouts). */
function resolvePenaltyAnalyticsUrl(pathOrUrl: string): string {
  const raw = pathOrUrl.trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw
  }
  const baseRaw = process.env.REACT_APP_API_BASE_URL?.trim().replace(/\/$/, '')
  if (
    baseRaw &&
    (baseRaw.startsWith('http://') || baseRaw.startsWith('https://'))
  ) {
    const path = raw.startsWith('/') ? raw : `/${raw}`
    return `${baseRaw}${path}`
  }
  return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`
}

/** STP penalty analytics listing (`REACT_APP_API_STP_ANALYTICS`, default `/api/penalty`). */
function buildStpAnalyticsUrl(): string {
  const trimmed = process.env.REACT_APP_API_STP_ANALYTICS?.trim()
  const pathOrUrl = trimmed || '/api/penalty'
  return resolvePenaltyAnalyticsUrl(pathOrUrl)
}

function coerceObjectRows(arr: unknown): AnalyticsRecord[] {
  if (!Array.isArray(arr)) return []
  return arr.filter(
    (x): x is AnalyticsRecord =>
      x !== null && typeof x === 'object' && !Array.isArray(x)
  )
}

function extractAnalyticsRows(json: unknown): AnalyticsRecord[] {
  const root = coerceObjectRows(json)
  if (root.length > 0) return root

  if (!json || typeof json !== 'object' || Array.isArray(json)) return []
  const o = json as Record<string, unknown>

  const wrapKeys = [
    'data',
    'items',
    'results',
    'records',
    'rows',
    'penalties',
    'stp',
    'analytics',
    'logs',
  ]

  for (const k of wrapKeys) {
    const inner = coerceObjectRows(o[k])
    if (inner.length > 0) return inner
  }

  const data = o.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const nested = coerceObjectRows(
      (data as Record<string, unknown>).rows
    )
    if (nested.length > 0) return nested
  }

  return []
}

/** Columns shown in Penalty Module View (fixed order). */
const PENALTY_ANALYTICS_DISPLAY_COLUMNS: readonly string[] = [
  'id',
  'total_penalty',
  'penalty_count',
  'penalty_message',
  'penalty_score',
  'penalty_date',
]

/** `total_penalty` → `Total penalty` (underscores → spaces); header styling is CSS bold. */
function penaltyColumnHeaderLabel(apiFieldKey: string): string {
  const spaced = apiFieldKey.replace(/_/g, ' ').trim()
  const lower = spaced.toLowerCase()
  if (!lower) return spaced
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

function isLikelyDateColumnKey(columnKey?: string): boolean {
  if (!columnKey) return false
  const k = columnKey.toLowerCase()
  return (
    k.endsWith('_at') ||
    k.endsWith('_date') ||
    k.includes('_date') ||
    k.includes('date') ||
    k === 'timestamp' ||
    k.includes('timestamp')
  )
}

/** ISO / RFC strings → `yyyy-mm-dd` (UTC calendar day for timestamps). */
function toYyyyMmDd(value: unknown): string | null {
  if (value === null || value === undefined) return null

  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms =
      value > 1e12 ? value : value > 1e9 ? Math.round(value * 1000) : value
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return null
    const y = d.getUTCFullYear()
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
    const da = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${mo}-${da}`
  }

  const text = String(value).trim()
  if (!text) return null

  const isoDay = text.match(/^(\d{4}-\d{2}-\d{2})/)
  if (isoDay) return isoDay[1]

  const d = new Date(text)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const da = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

function formatAnalyticsCell(value: unknown, columnKey?: string): string {
  if (value === null || value === undefined) return '—'
  if (isLikelyDateColumnKey(columnKey)) {
    const ymd = toYyyyMmDd(value)
    if (ymd) return ymd
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—'
    if (columnKey === 'total_penalty') return value.toFixed(2)
    return String(value)
  }
  if (typeof value === 'string') {
    const s = value.trim()
    return s === '' ? '—' : s
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : JSON.stringify(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function parseTotalPenaltyFromRow(rec: AnalyticsRecord): number {
  const keys = [
    'total_penalty',
    'totalPenalty',
    'penalty',
    'Penalty',
    'penalty_amount',
    'penaltyAmount',
    'amount',
  ]
  for (const k of keys) {
    const v = rec[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string') {
      const n = Number(String(v).replace(/[₹,\s]/g, ''))
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

function rowSelectionKey(rec: AnalyticsRecord, index: number): string {
  const id = rec.id ?? rec._id ?? rec.stp_id ?? rec.stpId
  if (id != null && String(id).trim() !== '') return `${String(id)}-${index}`
  return `stp-analytics-${index}`
}

const Penalty = () => {
  const history = useHistory()
  const [rows, setRows] = useState<AnalyticsRecord[]>([])
  const [columnKeys, setColumnKeys] = useState<string[]>(() => [
    ...PENALTY_ANALYTICS_DISPLAY_COLUMNS,
  ])
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [analyticsError, setAnalyticsError] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    const url = buildStpAnalyticsUrl()
    if (!url) {
      setAnalyticsError(
        'REACT_APP_API_STP_ANALYTICS URL could not be resolved.'
      )
      setAnalyticsLoading(false)
      return
    }

    let cancelled = false
    setAnalyticsLoading(true)
    setAnalyticsError(null)

    fetch(url, { method: 'GET' })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`STP analytics request failed (${res.status})`)
        }
        return res.json()
      })
      .then((json: unknown) => {
        if (cancelled) return
        const list = extractAnalyticsRows(json)
        setRows(list)
        setColumnKeys([...PENALTY_ANALYTICS_DISPLAY_COLUMNS])
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg =
            e instanceof TypeError && e.message === 'Failed to fetch'
              ? 'Could not reach STP analytics API. Check backend and REACT_APP_API_BASE_URL.'
              : e instanceof Error
                ? e.message
                : 'Failed to load STP analytics.'
          setAnalyticsError(msg)
          setRows([])
          setColumnKeys([...PENALTY_ANALYTICS_DISPLAY_COLUMNS])
        }
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (rows.length === 0) {
      setSelectedKey(null)
      return
    }
    setSelectedKey((prev) => {
      const firstKey = rowSelectionKey(rows[0], 0)
      if (prev === null) return firstKey
      const still = rows.some((r, i) => rowSelectionKey(r, i) === prev)
      return still ? prev : firstKey
    })
  }, [rows])

  const totalsDisplay = useMemo(() => {
    if (analyticsLoading) {
      return { amount: '…', violations: '…' }
    }
    const sum = rows.reduce((acc, row) => acc + parseTotalPenaltyFromRow(row), 0)
    const n = rows.length
    return {
      amount: formatInr(sum),
      violations: `${n} record${n === 1 ? '' : 's'}`,
    }
  }, [rows, analyticsLoading])

  const goToPlants = () => {
    history.push('/plants')
  }

  const goToContracts = () => {
    history.push('/contatcts')
  }

  const goToPenalty = () => {
    history.push('/penalty')
  }

  const goToDashboard = () => {
    history.push('/dashboard')
  }

  const goToReports = () => {
    history.push('/reports')
  }

  const goToMonitoring = () => {
    history.push('/monitoring')
  }

  const goToCctv = () => {
    history.push('/cctv')
  }

  const goToInventory = () => {
    history.push('/inventory')
  }

  const goToSettings = () => {
    history.push('/settings')
  }

  const handleSignOut = () => {
    history.push('/')
  }

  return (
    <div className="penalty-container1">
      <Helmet>
        <title>Penalty - exported project</title>
        <meta property="og:title" content="Penalty - exported project" />
        <link
          rel="canonical"
          href="https://namami-ganga-design-file-pvya6a.teleporthq.app/penalty"
        />
        <meta
          property="og:url"
          content="https://namami-ganga-design-file-pvya6a.teleporthq.app/penalty"
        />
      </Helmet>
      <div className="penalty-thq-penalty-elm">
        <span className="penalty-thq-text-elm10">Dashboard</span>
        <img
          src="/bg1118-lxqj-300w.png"
          alt="BG1118"
          className="penalty-thq-bg-elm"
        />
        <div className="penalty-thq-settings-elm1" onClick={goToPlants}>
          <span className="penalty-thq-text-elm11">Plants</span>
        </div>
        <div className="penalty-thq-settings-elm2" onClick={goToContracts}>
          <span className="penalty-thq-text-elm12">Contracts</span>
        </div>
        <div className="penalty-thq-settings-elm3" onClick={goToReports}>
          <span className="penalty-thq-text-elm13">Reports</span>
        </div>
        <div className="penalty-thq-settings-elm4" onClick={goToMonitoring}>
          <span className="penalty-thq-text-elm14">Real time monitoring</span>
        </div>
        <div className="penalty-thq-settings-elm5">
          <span className="penalty-thq-text-elm15">Manpower</span>
        </div>
        <div className="penalty-thq-settings-elm6" onClick={goToInventory}>
          <span className="penalty-thq-text-elm16">Inventory</span>
        </div>
        <span className="penalty-thq-text-elm17">Collaboration</span>
        <span className="penalty-thq-text-elm18" onClick={goToCctv} role="presentation">
          CCTV Monitoring
        </span>
        <span className="penalty-thq-text-elm19" onClick={goToSettings} role="presentation">
          Settings
        </span>
        <div
          className="penalty-thq-dashboard-elm"
          onClick={goToDashboard}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') goToDashboard()
          }}
          role="button"
          tabIndex={0}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
          <span className="penalty-thq-text-elm20">Dashboard</span>
          <img
            src="/rectangle2891120-ndpd-200h.png"
            alt="Rectangle2891120"
            className="penalty-thq-rectangle289-elm"
          />
        </div>
        <img
          src="/grid1120-j34b.svg"
          alt="Grid1120"
          className="penalty-thq-grid-elm"
        />
        <img
          src="/settings1120-cbv.svg"
          alt="Settings1120"
          className="penalty-thq-settings-elm7"
        />
        <img
          src="/ellipse37041120-za0o-200h.png"
          alt="Ellipse37041120"
          className="penalty-thq-ellipse3704-elm"
        />
        <img
          src="/ellipse37051120-ifso-200h.png"
          alt="Ellipse37051120"
          className="penalty-thq-ellipse3705-elm"
        />
        <img
          src="/line4771120-c2ag.svg"
          alt="Line4771120"
          className="penalty-thq-line477-elm"
        />
        <img
          src="/line4781120-rxjc.svg"
          alt="Line4781120"
          className="penalty-thq-line478-elm"
        />
        <img
          src="/chevron1120-kk1e.svg"
          alt="Chevron1120"
          className="penalty-thq-chevron-elm"
        />
        <img
          src="/image1120-bwth-200h.png"
          alt="Image1120"
          className="penalty-thq-image-elm"
        />
        <span className="penalty-thq-text-elm21">Rajesh Nair</span>
        <span className="penalty-thq-text-elm22">Super Admin</span>
        <img
          src="/arrowexit1121-yl5j.svg"
          alt="Sign out"
          className="penalty-thq-arrow-exit-elm"
          onClick={handleSignOut}
        />
        <img
          src="/alert1121-yee.svg"
          alt="Alert1121"
          className="penalty-thq-alert-elm"
        />
        <div className="penalty-thq-frame99-elm">
          <div className="penalty-thq-frame101-elm1">
            <span className="penalty-thq-text-elm23">Active Sites</span>
          </div>
          <span className="penalty-thq-text-elm24">34</span>
        </div>
        <div className="penalty-thq-frame100-elm">
          <div className="penalty-thq-frame101-elm2">
            <span className="penalty-thq-text-elm25">Green Plants</span>
          </div>
          <span className="penalty-thq-text-elm26">32</span>
        </div>
        <div className="penalty-thq-frame102-elm">
          <div className="penalty-thq-frame101-elm3">
            <span className="penalty-thq-text-elm27">Critical Alerts</span>
          </div>
        </div>
        <div className="penalty-thq-frame1984080068-elm">
          <div className="penalty-thq-frame101-elm4">
            <span className="penalty-thq-text-elm28">Total Penalties</span>
          </div>
          <span className="penalty-thq-text-elm29">{totalsDisplay.amount}</span>
          <img
            src="/rectangle1301123-nle-200h.png"
            alt="Rectangle1301123"
            className="penalty-thq-rectangle130-elm"
          />
          <span className="penalty-thq-text-elm30">{totalsDisplay.violations}</span>
        </div>
        <img
          src="/namamigangelogohindi11123-19br-200h.png"
          alt="NamamiGangeLogoHindi11123"
          className="penalty-thq-namami-gange-logo-hindi1-elm"
        />
        <div className="penalty-thq-frame1984080069-elm">
          <span className="penalty-thq-text-elm31">Penalty Module View</span>
          <div className="penalty-thq-frame1984080071-elm">
            <div className="penalty-logs-inner">
              <div className="penalty-logs-body">
                {analyticsLoading && (
                  <div className="penalty-logs-status">
                    Loading STP analytics…
                  </div>
                )}
                {!analyticsLoading && analyticsError && (
                  <div className="penalty-logs-status penalty-logs-error">
                    {analyticsError}
                  </div>
                )}
                {!analyticsLoading && !analyticsError && (
                  <table className="penalty-logs-table">
                    <thead>
                      <tr>
                        {columnKeys.map((col) => (
                          <th key={col} scope="col">
                            {penaltyColumnHeaderLabel(col)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr className="penalty-logs-row-empty">
                          <td
                            className="penalty-logs-status penalty-logs-empty"
                            colSpan={Math.max(columnKeys.length, 1)}
                          >
                            No STP analytics rows returned.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, rowIdx) => {
                          const key = rowSelectionKey(row, rowIdx)
                          return (
                            <tr
                              key={key}
                              className={
                                selectedKey === key
                                  ? 'penalty-logs-row-selected'
                                  : undefined
                              }
                              aria-selected={selectedKey === key}
                              tabIndex={0}
                              onClick={() => setSelectedKey(key)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setSelectedKey(key)
                                }
                              }}
                            >
                              {columnKeys.map((col) => (
                                <td key={col}>
                                  {formatAnalyticsCell(row[col], col)}
                                </td>
                              ))}
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
        <span className="penalty-thq-text-elm64" onClick={goToPenalty}>
          Penalty
        </span>
      </div>
    </div>
  )
}

export default Penalty

